import React, { CSSProperties, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Sheet as RomaSheet, SheetBehaviourProps } from '@milanote/roma';

import globalLogger from '../../../logger/logger';
import { useTranslator } from '../../../components/i18n/useTranslator';
import { getPlatformDetailsSelector } from '../../../platform/platformSelector';
import useLockScreenOrientationPortrait from '../../../platform/screenOrientation/useLockScreenOrientationPortrait';
import { shouldUseNativeAndroidKeyboardResize } from '../../../platform/utils/platformDetailsUtils';
import useSafeAreaInset from '../../utils/useSafeAreaInset';
import SheetPortal from './SheetPortal';
import useSheetAnimationDebug from './hooks/useSheetAnimationDebug';
import useSheetObstructionHandler from './hooks/useSheetObstructionHandler';
import { UseSheetReturnType } from './hooks/useSheet';
import sheetConfig from './sheetConfig';
import { blurActiveTextInput } from '../../../utils/dom/domInputUtils';

const logger = globalLogger.createChannel('mobile.sheet');

const TOOLBAR_STYLE: CSSProperties = { zIndex: 'var(--z-index-sheet-with-toolbar)' };

export type SheetProps = SheetBehaviourProps &
    // The whole `useSheet` return value is spread onto the sheet, so the app-only pieces land here too
    Partial<Omit<UseSheetReturnType, keyof SheetBehaviourProps>> & {
        children: React.ReactNode;
        className?: string;
        /** Raises the sheet above the mobile toolbar. */
        showToolbar?: boolean;
        /** Keeps the virtual keyboard visible while the sheet is dragged. */
        preserveKeyboardOnDrag?: boolean;
    };

/**
 * The app's bottom sheet: a Roma `<Sheet>` in the shared portal, plus the redux and Capacitor
 * behaviour Roma stays out of.
 *
 * Compose it from the same parts as the Roma sheet — `Sheet.Overlay`, `Sheet.Panel`, `Sheet.Header`,
 * `Sheet.Heading`, `Sheet.Content` and the header buttons — and drive it with `useSheet`:
 *
 *     const sheetProps = useSheet(SheetId.Notifications);
 *     <Sheet {...sheetProps} defaultSnapPoint={1} snapPoints={[0.5, 1]}>…</Sheet>
 *
 * All of the platform glue is read *here* rather than in `useSheet` on purpose: this component
 * owns none of the sheet's content, so its own re-renders leave the children untouched, whereas a
 * subscription in consumer scope re-renders the consumer and forces React to reconcile the entire
 * sheet subtree — in the same frame the open transition starts.
 *
 * See docs/sheet-component.md for more detail.
 */
const SheetRoot = ({
    children,
    className,
    showToolbar,

    // App-only pieces of `useSheet`'s return value, which the sheet itself has no use for
    sheetId,
    data,
    openSheet,
    closeSheet,
    sheetKey,

    completeCloseTransition,
    onCloseTransitionEnd,
    onDragStart,
    preserveKeyboardOnDrag = false,

    ...sheetProps
}: SheetProps) => {
    const { isOpen, activeSnapPoint, snapPoints, onSnapPointChange } = sheetProps;

    const useViewportResizeForKeyboard = useSelector((state: unknown) =>
        shouldUseNativeAndroidKeyboardResize(getPlatformDetailsSelector(state)),
    );

    const topInset = useSafeAreaInset('top');

    const animationDebugHandlers = useSheetAnimationDebug();

    useLockScreenOrientationPortrait(isOpen);

    const { cancelReturnSnapPoint } = useSheetObstructionHandler({
        sheetKey,
        isOpen,
        activeSnapPoint,
        snapPoints,
        onSnapPointChange,
    });

    const handleCloseTransitionEnd = useCallback(() => {
        completeCloseTransition?.();
        onCloseTransitionEnd?.();
    }, [completeCloseTransition, onCloseTransitionEnd]);

    const handleDragStart = useCallback(() => {
        if (!preserveKeyboardOnDrag) blurActiveTextInput();

        cancelReturnSnapPoint();
        onDragStart?.();
    }, [cancelReturnSnapPoint, onDragStart, preserveKeyboardOnDrag]);

    const onCloseTransitionTimeout = useCallback(
        () =>
            logger.warn('Sheet close transitionend event did not fire in time, timeout fallback ran instead', {
                sheetId,
                forwardSampleRate: 0.001,
            }),
        [sheetId],
    );

    return (
        <SheetPortal>
            <RomaSheet
                {...sheetProps}
                topInset={topInset}
                useViewportResizeForKeyboard={useViewportResizeForKeyboard}
                // Element editor sheets have their content height driven by mobileEditingMiddleware
                contentHeightIgnoresKeyboard={!!(sheetId && sheetConfig[sheetId]?.containsElementEditor)}
                onCloseTransitionEnd={handleCloseTransitionEnd}
                onCloseTransitionTimeout={onCloseTransitionTimeout}
                onDragStart={handleDragStart}
                {...animationDebugHandlers}
                className={className}
                style={showToolbar ? TOOLBAR_STYLE : undefined}
            >
                {children}
            </RomaSheet>
        </SheetPortal>
    );
};

SheetRoot.displayName = 'Sheet';

/** Roma's `Sheet.DoneButton` owns no copy, so the translated default lives here. */
const DoneButton = ({
    children,
    className,
    onClick,
}: {
    children?: React.ReactNode;
    className?: string;
    onClick?: () => void;
}) => {
    const translate = useTranslator('userInterface', 'mobile');

    return (
        <RomaSheet.DoneButton className={className} onClick={onClick}>
            {children ?? translate('done')}
        </RomaSheet.DoneButton>
    );
};

DoneButton.displayName = 'SheetDoneButton';

const CloseButton = ({
    children,
    className,
    label,
    onClick,
}: {
    children?: React.ReactNode;
    className?: string;
    label?: string;
    onClick?: () => void;
}) => {
    const translate = useTranslator('userInterface', 'mobile');

    return (
        <RomaSheet.CloseButton className={className} label={label} onClick={onClick}>
            {children ?? translate('close')}
        </RomaSheet.CloseButton>
    );
};

CloseButton.displayName = 'SheetCloseButton';

const BackButton = ({ className, onClick }: { className?: string; onClick?: () => void }) => {
    const translate = useTranslator('userInterface', 'mobile');

    return <RomaSheet.BackButton className={className} label={translate('back')} onClick={onClick} />;
};

BackButton.displayName = 'SheetBackButton';

export const Sheet = Object.assign(SheetRoot, {
    DoneButton,
    CloseButton,
    BackButton,
    Overlay: RomaSheet.Overlay,
    Panel: RomaSheet.Panel,
    Header: RomaSheet.Header,
    Heading: RomaSheet.Heading,
    Content: RomaSheet.Content,
    NonDraggableArea: RomaSheet.NonDraggableArea,
});
